/**
 * guitar-tuner-tool.js — the page of /guitar-tuner: microphone, strings row,
 * readout. Everything that decides WHAT to say lives in guitar-tuner-core.js
 * (node-tested); this file only measures, draws and reports.
 *
 * Config from the blade: window.GT_T (strings of the page, one locale).
 */
(function () {
    'use strict';

    var SLUG = 'guitar-tuner';

    function boot() {
        var Core = window.GuitarTunerCore;
        if (!Core || !document.getElementById('gtStrings')) return false;
        var T = window.GT_T || {};
        var $ = function (id) { return document.getElementById(id); };
        var tr = function (key, rep) {
            var s = T[key] || '';
            if (rep) for (var k in rep) if (Object.prototype.hasOwnProperty.call(rep, k)) s = s.split(':' + k).join(rep[k]);
            return s;
        };
        var ae = function (type, data) { try { if (window.__ae) window.__ae(type, data); } catch (_) { } };

        var noteEl = $('gtNote'), centsEl = $('gtCents'), needleEl = $('gtNeedle'), freqEl = $('gtFreq');
        var pitchbar = $('gtPitchbar'), micBtn = $('gtMicBtn'), micBtnText = $('gtMicBtnText');
        var micState = $('gtMicState'), micStateText = $('gtMicStateText');
        var stringsWrap = $('gtStrings'), errEl = $('gtErr'), switchBtn = $('gtSwitch'), a4Chip = $('gtA4Chip');
        var tuningSel = $('gtTuning'), a4Input = $('gtA4'), modeAuto = $('gtModeAuto'), modeManual = $('gtModeManual');

        var tuning = tuningSel.value || 'standard', a4 = parseInt(a4Input.value, 10) || 440;
        var locked = null;                  // string NUMBER (6 = thick E) or null = auto
        var strs = Core.strings(tuning, a4), flats = Core.usesFlats(tuning);
        var audioCtx = null, source = null, analyser = null, buf = null, detector = null, tracker = null;
        var rafId = null, stream = null, mic = null, starting = false;
        var ref = null, shown = {};
        // telemetry: one line per guard per note, one «in tune» per string
        var noteSerial = 0, lastOnset = -1, guardSent = {}, guardCount = 0, inTuneSince = 0, inTuneSent = {}, completed = false;

        // ── strings row ─────────────────────────────────────────────────
        function noteHtml(name, oct) { return name + '<sup class="gt-note-octave">' + oct + '</sup>'; }
        function renderStrings() {
            shown.mark = null;                 // the cards the marks were on are gone
            /* A4 is remembered between visits; a reference other than 440
               moves every string's target, so it is shown next to the
               reading, not only in the settings row. */
            a4Chip.hidden = a4 === 440;
            a4Chip.textContent = 'A4 = ' + a4 + ' Hz';
            strs = Core.strings(tuning, a4);
            flats = Core.usesFlats(tuning);
            stringsWrap.innerHTML = '';
            strs.forEach(function (s) {
                var btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'gt-string' + (locked === s.num ? ' active' : '');
                btn.setAttribute('data-string', String(s.num));
                btn.setAttribute('aria-pressed', locked === s.num ? 'true' : 'false');
                btn.setAttribute('aria-label', tr('string_aria', { num: s.num, note: s.name + s.oct, hz: s.freq.toFixed(1) }));
                btn.innerHTML = '<span class="num">' + s.num + '</span><span class="note">' + noteHtml(s.name, s.oct) + '</span>' +
                    '<span class="freq">' + s.freq.toFixed(1) + ' Hz</span><span class="ref-btn" role="button" tabindex="0">' + (T.play_ref || '') + '</span>';
                btn.addEventListener('click', function (e) {
                    if (e.target && e.target.classList && e.target.classList.contains('ref-btn')) {
                        e.stopPropagation();
                        playReference(s.freq);
                        return;
                    }
                    selectString(s.num);
                });
                var refEl = btn.querySelector('.ref-btn');
                refEl.addEventListener('keydown', function (e) {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); playReference(s.freq); }
                });
                stringsWrap.appendChild(btn);
            });
        }
        function cardOf(num) { return stringsWrap.querySelector('.gt-string[data-string="' + num + '"]'); }

        // ── modes: one door, so the toggle and a tapped card never disagree ──
        function setMode(m) {
            if (m === 'auto') locked = null;
            modeAuto.classList.toggle('active', m === 'auto');
            modeManual.classList.toggle('active', m === 'manual');
            modeAuto.setAttribute('aria-pressed', m === 'auto' ? 'true' : 'false');
            modeManual.setAttribute('aria-pressed', m === 'manual' ? 'true' : 'false');
            renderStrings();
            if (tracker) render(lastReading);
        }
        /* A tap chooses the string in any mode (a card that looks like a
           button must act like one); tapping the chosen string again returns
           to auto. */
        function selectString(num) {
            if (locked === num) { setMode('auto'); ae('feature_use', { slug: SLUG, action: 'string_tap', num: num, lock: 0, tuning: tuning }); return; }
            locked = num;
            setMode('manual');
            ae('feature_use', { slug: SLUG, action: 'string_tap', num: num, lock: 1, tuning: tuning });
        }
        modeAuto.addEventListener('click', function () { setMode('auto'); });
        modeManual.addEventListener('click', function () { setMode('manual'); });
        switchBtn.addEventListener('click', function () {
            var n = parseInt(switchBtn.getAttribute('data-num'), 10);
            if (n) { ae('feature_use', { slug: SLUG, action: 'guard_switch', num: n }); locked = n; setMode('manual'); }
        });

        // ── reference tone ──────────────────────────────────────────────
        /* A plucked tone, not a sine: a phone speaker reproduces nothing at
           82 Hz, and a pure sine on the low strings was silence. The
           harmonics carry the pitch (the ear hears the missing fundamental). */
        function ensureCtx() {
            audioCtx = window.__liveAudioCtx ? window.__liveAudioCtx(audioCtx)
                : (audioCtx && audioCtx.state !== 'closed' ? audioCtx : new (window.AudioContext || window.webkitAudioContext)());
            if (audioCtx.state === 'suspended') audioCtx.resume().catch(function () { });
            return audioCtx;
        }
        var waves = {};
        function pluckWave(ctx) {
            if (waves.ctx === ctx && waves.w) return waves.w;
            var N = 16, re = new Float32Array(N + 1), im = new Float32Array(N + 1);
            for (var k = 1; k <= N; k++) im[k] = Math.abs(Math.sin(k * Math.PI / 5)) / Math.pow(k, 1.1);
            waves = { ctx: ctx, w: ctx.createPeriodicWave(re, im) };
            return waves.w;
        }
        function playReference(freq) {
            var ctx = ensureCtx();
            if (ref) { try { ref.osc.stop(); } catch (_) { } ref = null; }
            var osc = ctx.createOscillator(), g = ctx.createGain(), t = ctx.currentTime;
            osc.setPeriodicWave(pluckWave(ctx));
            osc.frequency.value = freq;
            g.gain.setValueAtTime(0, t);
            g.gain.linearRampToValueAtTime(0.3, t + 0.006);
            g.gain.setTargetAtTime(0.0001, t + 0.05, 0.7);
            osc.connect(g); g.connect(ctx.destination);
            osc.start(t); osc.stop(t + 2.6);
            ref = { osc: osc };
            ae('feature_use', { slug: SLUG, action: 'ref_tone', hz: Math.round(freq) });
        }

        // ── microphone ──────────────────────────────────────────────────
        function showErr(msg) { errEl.textContent = msg; errEl.classList.remove('hidden'); }
        function hideErr() { errEl.classList.add('hidden'); }
        function setMicState(s) {
            micState.classList.remove('live', 'error');
            if (s === 'live') { micState.classList.add('live'); micStateText.textContent = T.state_listen || ''; }
            else if (s === 'error') { micState.classList.add('error'); micStateText.textContent = T.state_error || ''; }
            else micStateText.textContent = T.state_idle || '';
        }
        function micManager() {
            if (mic || typeof window.MicManager !== 'function') return mic;
            mic = new window.MicManager({
                locale: window.__LOCALE, slug: SLUG,
                audioContext: function () { return audioCtx; },
                onRetry: function () { startMic(); }
            });
            return mic;
        }
        function startMic() {
            if (stream || starting) return;
            hideErr();
            var m = micManager();
            if (!m) { showErr(T.err_no_mic || ''); return; }
            starting = true;
            ensureCtx();                        // inside the tap: iOS resumes a context only here
            micBtn.setAttribute('aria-busy', 'true');
            m.startRaw({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }).then(function (s) {
                starting = false;
                micBtn.removeAttribute('aria-busy');
                stream = s;
                ensureCtx();
                source = audioCtx.createMediaStreamSource(stream);
                analyser = audioCtx.createAnalyser();
                var size = Core.frameSize(audioCtx.sampleRate);
                analyser.fftSize = Math.min(32768, size);
                buf = new Float32Array(analyser.fftSize);
                detector = new Core.Detector({ sampleRate: audioCtx.sampleRate, size: analyser.fftSize, fft: window.DspFft || null });
                tracker = new Core.Tracker();
                source.connect(analyser);
                pitchbar.classList.remove('muted');
                setMicState('live');
                micBtn.classList.add('live');
                micBtnText.textContent = T.stop_mic || '';
                if (window.__wakeLock) window.__wakeLock.request().catch(function () { });
                var trk = stream.getAudioTracks && stream.getAudioTracks()[0], st = {};
                try { st = (trk && trk.getSettings && trk.getSettings()) || {}; } catch (_) { }
                ae('feature_use', { slug: SLUG, action: 'mic_start', sr: audioCtx.sampleRate, tsr: st.sampleRate || 0, fft: analyser.fftSize, fftjs: window.DspFft ? 1 : 0, a4: a4, tuning: tuning });
                loop();
            }, function (err) {
                starting = false;
                micBtn.removeAttribute('aria-busy');
                // MicManager has classified the cause and shown its own help;
                // keep its sentence here too, the modal can be dismissed
                showErr((err && err.message) || T.err_no_mic || '');
                setMicState('error');
            });
        }
        function stopMic() {
            if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
            if (source) { try { source.disconnect(); } catch (_) { } source = null; }
            if (mic) { try { mic.stop(); } catch (_) { } }
            else if (stream) stream.getTracks().forEach(function (t) { t.stop(); });
            stream = null; analyser = null; tracker = null; lastReading = null;
            if (window.__wakeLock) { try { window.__wakeLock.release(); } catch (_) { } }
            pitchbar.classList.add('muted');
            setMicState('idle');
            micBtn.classList.remove('live');
            micBtnText.textContent = T.start_mic || '';
            render(null);
        }
        micBtn.addEventListener('click', function () { if (stream) stopMic(); else startMic(); });
        document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden' && stream) stopMic(); });

        // ── the loop ────────────────────────────────────────────────────
        var lastReading = null;
        function loop() {
            if (!analyser) return;
            rafId = requestAnimationFrame(loop);
            analyser.getFloatTimeDomainData(buf);
            var t = performance.now();
            var a = detector.analyze(buf);
            var r = tracker.push(a, t);
            if (tracker.onsetAt !== lastOnset) { lastOnset = tracker.onsetAt; noteSerial++; }
            lastReading = r;
            render(r);
        }

        // ── the readout ─────────────────────────────────────────────────
        /* Written every animation frame, so only what changed is touched; and
           a screen reader hears the state (in tune / other string / far), not
           sixty cents values a second. */
        function put(key, el, prop, val) { if (shown[key] !== val) { shown[key] = val; el[prop] = val; } }
        function mark(num, cls) {
            var want = num ? num + ':' + cls : '';
            if (shown.mark === want) return;
            shown.mark = want;
            stringsWrap.querySelectorAll('.gt-string').forEach(function (c) { c.classList.remove('in-tune', 'suggest', 'heard'); });
            var c = num && cardOf(num); if (c) c.classList.add(cls);
        }
        var said = '';
        function announce(kind, text) {
            if (kind === said) return;
            said = kind;
            if (kind && window.__announce && (kind === 'ok' || kind === 'other' || kind === 'far')) { try { window.__announce(text); } catch (_) { } }
        }
        function setNeedle(cents, on) {
            put('needle', needleEl.style, 'left', (50 + Math.max(-50, Math.min(50, on ? cents : 0))) + '%');
            if (shown.needleOn !== on) { shown.needleOn = on; pitchbar.classList.toggle('muted', !on); }
        }
        function render(r) {
            if (!r) {
                put('note', noteEl, 'textContent', '—');
                noteEl.classList.add('muted');
                put('cls', centsEl, 'className', 'gt-cents-label');
                put('label', centsEl, 'textContent', (locked === null && modeManual.classList.contains('active')) ? (T.pick_string || '') : (T.cents_hint || ''));
                put('freq', freqEl, 'textContent', '—');
                setNeedle(0, false);
                mark(0, '');
                switchBtn.hidden = true;
                announce('', '');
                inTuneSince = 0;
                return;
            }
            var adv = Core.advise(r.freq, strs, { locked: locked, flats: flats, a4: a4 });
            noteEl.classList.toggle('muted', !!r.held);
            put('note', noteEl, 'innerHTML', noteHtml(adv.note.name, adv.note.oct));
            var tg = adv.target, tname = tg ? tg.name + tg.oct : '', cls = 'gt-cents-label', label;
            if (adv.kind === 'ok') { cls += ' ok'; label = '✓ ' + (T.cents_ok || '') + ' → ' + tname; }
            else if (adv.kind === 'tighten' || adv.kind === 'loosen') {
                cls += Math.abs(adv.cents) < 15 ? ' warn' : ' bad';
                label = (adv.cents > 0 ? '+' : '') + adv.cents + '¢ · ' + (adv.kind === 'tighten' ? T.cents_low : T.cents_high) + ' → ' + tname;
            } else if (adv.kind === 'other') {
                cls += ' warn';
                label = tr('other', { heard: adv.other.num, note: adv.other.name + adv.other.oct, num: tg.num });
                put('sw', switchBtn, 'textContent', tr('switch_to', { num: adv.other.num }));
                switchBtn.setAttribute('data-num', String(adv.other.num));
            } else {
                cls += ' warn';
                label = locked === null ? (T.far_auto || '') : tr(adv.cents < 0 ? 'far_low' : 'far_high', { num: tg.num, note: tname });
            }
            switchBtn.hidden = adv.kind !== 'other';
            put('cls', centsEl, 'className', cls);
            put('label', centsEl, 'textContent', label);
            var guided = adv.kind === 'ok' || adv.kind === 'tighten' || adv.kind === 'loosen';
            setNeedle(adv.cents, guided && !r.held);
            put('freq', freqEl, 'textContent', r.freq.toFixed(2) + ' Hz' + (tg && guided ? ' / ' + tname + ' ' + tg.freq.toFixed(2) + ' Hz' : ''));
            if (adv.kind === 'other') mark(adv.other.num, 'suggest');
            else mark(tg && guided ? tg.num : 0, adv.kind === 'ok' ? 'in-tune' : 'heard');
            if (!r.held) announce(adv.kind, label);
            report(adv, r);
        }

        function report(adv, r) {
            if (r.held) return;
            var now = performance.now();
            if (adv.kind === 'other' || adv.kind === 'far') {
                var key = noteSerial + ':' + adv.kind;
                if (!guardSent[key] && guardCount < 30) {
                    guardSent[key] = 1; guardCount++;
                    ae('feature_use', { slug: SLUG, action: 'guard', kind: adv.kind, mode: locked === null ? 'auto' : 'lock', target: adv.target ? adv.target.num : 0, heard: adv.other ? adv.other.num : 0, cents: adv.cents, tuning: tuning });
                }
            }
            if (adv.kind === 'ok') {
                if (!inTuneSince) inTuneSince = now;
                if (now - inTuneSince >= 1000 && !inTuneSent[tuning + adv.target.num]) {
                    inTuneSent[tuning + adv.target.num] = 1;
                    ae('feature_use', { slug: SLUG, action: 'in_tune', num: adv.target.num, mode: locked === null ? 'auto' : 'lock', tuning: tuning });
                    if (!completed && window.__aeToolComplete) { completed = true; try { window.__aeToolComplete({ in_tune: adv.target.num, tuning: tuning }); } catch (_) { } }
                }
            } else inTuneSince = 0;
        }

        // ── settings ────────────────────────────────────────────────────
        if (window.__toolSettings) {
            window.__toolSettings.bind(SLUG, tuningSel, 'tune', 'standard');
            window.__toolSettings.bind(SLUG, a4Input, 'a4', '440');
            tuning = Core.TUNINGS[tuningSel.value] ? tuningSel.value : 'standard';
            a4 = parseInt(a4Input.value, 10) || 440;
        }
        tuningSel.addEventListener('change', function () {
            tuning = Core.TUNINGS[this.value] ? this.value : 'standard';
            locked = null;
            setMode('auto');
            ae('feature_use', { slug: SLUG, action: 'tuning', value: tuning });
        });
        a4Input.addEventListener('change', function () {
            var v = parseInt(this.value, 10) || 440;
            v = Math.max(415, Math.min(466, v));
            this.value = v; a4 = v;
            renderStrings();
        });

        renderStrings();
        render(null);
        return true;
    }

    function start() {
        if (boot()) return;
        /* A script that came in through the fallback ladder arrives after the
           document; try again when everything has loaded. */
        window.addEventListener('load', function () {
            if (boot()) return;
            var e = document.getElementById('gtErr');
            if (e && window.GT_T) { e.textContent = window.GT_T.module_failed || ''; e.classList.remove('hidden'); }
        }, { once: true });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
})();
